import { relations, sql } from 'drizzle-orm'
import {
  AnyPgColumn,
  check,
  index,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getNullableIdReference,
  typedJsonb,
} from '../common/utils'
import { addresses } from './address'
import { customerMembers } from './customerMember'
import { properties } from './property'
import { customerTags } from './tag'
import { users } from './user'

// Customer type enum
export const customerTypeEnum = pgEnum('customer_type', [
  'household',
  'housing_company',
  'company',
  'agency',
])

// Customer status enum
export const customerStatusEnum = pgEnum('customer_status', [
  'active',
  'paused',
  'churned',
  'prospect',
])

// Billing method enum
export const billingMethodEnum = pgEnum('billing_method', ['email', 'einvoice'])

export const customers = pgTable(
  'customers',
  {
    ...getBaseFields(),
    name: text().notNull(),
    // Finnish Y-tunnus — the canonical identifier for B2B organizations
    // (housing companies, agencies, companies). Populated from the asoy
    // prospecting import; households have none. Deduped by the partial
    // unique index below.
    businessId: text(),
    // Office / building address. Agencies and prospect taloyhtiöt have no
    // `properties` row, so the organization's own location lives here.
    // Nullable; populated from the asoy import — used by audience / region
    // filtering (e.g. the isännöitsijä calling campaign).
    addressId: getNullableIdReference(() => addresses.id),
    type: customerTypeEnum().notNull().default('household'),
    status: customerStatusEnum().notNull().default('active'),
    // Staff-only account commentary. The `internal` prefix hides it from the
    // customer's own members, who can otherwise read their customer row.
    internalNotes: text(),
    stripeCustomerId: text(),
    billingMethod: billingMethodEnum().notNull().default('email'),
    billingEmail: text(),
    // The managing property-management agency for a housing company. Set only
    // on type='housing_company' rows; references a type='agency' customer.
    // Current-state only (no history). The housing_company -> agency type pair
    // is enforced by server-side validation in the customer upsert path; the
    // non-self rule is enforced by the CHECK constraint below.
    managedByCustomerId: getNullableIdReference(
      (): AnyPgColumn => customers.id,
    ),
    // Konsernirakenne — an agency's corporate parent (isännöinti HQ → branch
    // offices). A second, separate self-FK; agency -> agency only, enforced by
    // server-side validation, with the non-self CHECK constraint below.
    parentCustomerId: getNullableIdReference((): AnyPgColumn => customers.id),
    // The key account manager (KAM) responsible for this customer. Standing
    // account-ownership metadata — distinct from `visits.salesUserId`, which
    // records who sold a specific visit and drives sales commission. Set
    // independently per customer (no inheritance from a managing agency).
    accountManagerUserId: getNullableIdReference(() => users.id),
    // Marketing attribution captured the first time we created this customer.
    // Write-once; `internal` prefix hides it from non-staff roles.
    internalFirstTouchAcquisition: typedJsonb(
      'internal_first_touch_acquisition',
    ),
  },
  (table) => [
    ...getBaseIndexes('customers', table),
    check(
      'customers_managed_by_not_self',
      sql`${table.managedByCustomerId} IS NULL OR ${table.managedByCustomerId} <> ${table.id}`,
    ),
    check(
      'customers_parent_not_self',
      sql`${table.parentCustomerId} IS NULL OR ${table.parentCustomerId} <> ${table.id}`,
    ),
    // B2B dedup — a Y-tunnus is unique among non-deleted customers.
    uniqueIndex('customers_business_id_unique_idx')
      .on(table.businessId)
      .where(
        sql`${table.businessId} IS NOT NULL AND ${table.deletedAt} IS NULL`,
      ),
    // Filtered by account manager ("my accounts", funnel customerId resolution).
    index('customers_account_manager_user_id_idx').on(
      table.accountManagerUserId,
    ),
  ],
)

export const customersRelations = relations(customers, ({ one, many }) => ({
  members: many(customerMembers),
  properties: many(properties),
  address: one(addresses, {
    fields: [customers.addressId],
    references: [addresses.id],
  }),
  managedByAgency: one(customers, {
    fields: [customers.managedByCustomerId],
    references: [customers.id],
    relationName: 'agencyManagement',
  }),
  managedHousingCompanies: many(customers, {
    relationName: 'agencyManagement',
  }),
  parentCustomer: one(customers, {
    fields: [customers.parentCustomerId],
    references: [customers.id],
    relationName: 'konserni',
  }),
  branchOffices: many(customers, {
    relationName: 'konserni',
  }),
  accountManager: one(users, {
    fields: [customers.accountManagerUserId],
    references: [users.id],
  }),
  // `internal` prefix is load-bearing: customer.getById is readable by the
  // `user` role, and omitInternalFields strips internal* keys recursively.
  // Renaming this leaks sales tags into the resident app.
  internalCustomerTags: many(customerTags),
}))
